import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiAward, FiUsers, FiTrendingUp, FiShield, FiClock, FiHeart } = FiIcons;

const Trust = () => {
  const stats = [
    { icon: FiUsers, number: "500+", label: "Dental Practices", color: "from-dental-blue to-ai-teal" },
    { icon: FiTrendingUp, number: "35%", label: "Average Revenue Increase", color: "from-growth-green to-energy-coral" },
    { icon: FiClock, number: "24/7", label: "AI Availability", color: "from-ai-teal to-growth-green" },
    { icon: FiHeart, number: "98%", label: "Patient Satisfaction", color: "from-energy-coral to-dental-blue" }
  ];

  const testimonials = [
    {
      name: "Dr. Sarah Johnson",
      practice: "Smile Dental Care",
      text: "Donna has transformed our practice. We've seen a 40% increase in appointment bookings since implementation.",
      rating: 5
    },
    {
      name: "Dr. Michael Chen",
      practice: "Downtown Dentistry",
      text: "The AI never misses a call and handles insurance verification seamlessly. Our staff can focus on patient care.",
      rating: 5
    },
    {
      name: "Dr. Lisa Rodriguez",
      practice: "Family Dental Group",
      text: "ROI was evident within the first month. EngageDent Ai has become an essential part of our practice.",
      rating: 5
    }
  ];

  return (
    <section id="trust" className="py-24 bg-gradient-to-b from-gray-50 to-clean-white">
      <div className="max-w-7xl mx-auto px-6">
        {/* Trust Stats */}
        <motion.div
          className="text-center mb-20"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <h2 className="text-4xl md:text-5xl font-bold text-slate-gray mb-6">
            Trusted by{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-dental-blue to-ai-teal">
              Leading Practices
            </span>
          </h2>
          <p className="text-xl text-slate-gray opacity-80 max-w-4xl mx-auto leading-relaxed mb-16">
            Join hundreds of dental practices that have transformed their operations with EngageDent Ai
          </p>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <motion.div
                key={stat.label}
                className="text-center"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <div className={`w-20 h-20 bg-gradient-to-br ${stat.color} rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg`}>
                  <SafeIcon icon={stat.icon} className="text-3xl text-clean-white" />
                </div>
                <div className="text-4xl font-bold text-slate-gray mb-2">{stat.number}</div>
                <p className="text-slate-gray opacity-80 font-medium">{stat.label}</p>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Testimonials */}
        <motion.div
          className="mb-20"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          viewport={{ once: true }}
        >
          <h3 className="text-3xl font-bold text-slate-gray text-center mb-12">
            What Dental Professionals Say
          </h3>

          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={testimonial.name}
                className="bg-clean-white rounded-2xl p-8 shadow-lg border border-gray-100 hover:shadow-xl transition-all duration-300"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                whileHover={{ y: -5 }}
              >
                {/* Rating Stars */}
                <div className="flex mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <span key={i} className="text-energy-coral text-xl">★</span>
                  ))}
                </div>

                <p className="text-slate-gray leading-relaxed mb-6 italic">
                  "{testimonial.text}"
                </p>

                <div className="border-t border-gray-100 pt-4">
                  <p className="font-semibold text-slate-gray">{testimonial.name}</p>
                  <p className="text-sm text-slate-gray opacity-70">{testimonial.practice}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Trust Badges */}
        <motion.div
          className="text-center"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.6 }}
          viewport={{ once: true }}
        >
          <div className="flex flex-wrap justify-center items-center gap-8 opacity-60">
            <div className="flex items-center space-x-2">
              <SafeIcon icon={FiShield} className="text-2xl text-dental-blue" />
              <span className="font-semibold text-slate-gray">HIPAA Compliant</span>
            </div>
            <div className="flex items-center space-x-2">
              <SafeIcon icon={FiAward} className="text-2xl text-dental-blue" />
              <span className="font-semibold text-slate-gray">Industry Certified</span>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Trust;